import { useState } from "react";
import { Button } from "@mui/material";
import { SuccessCheckmarkIcon } from "../../Icons/Icons";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  getPredictionData,
  getPredictionSubmitState,
} from "../../store/selectors/PredictionSelectors";
import { getUploadedLocatorData } from "../../store/selectors/LocatorSelectors";
import {
  resetPredictionSlice,
  updatePredictionStep,
} from "../../store/reducers/PredictionReducers";
import { resetLocatorSlice } from "../../store/reducers/LocatorsReducers";
import LocatorDropdownProps from "../DropdownProps";
import { onAddLocatorToDatasetAction } from "../../store/actions/PredictionActions";
import Loader from "../Loader";
import SystemMsg from "../SystemMsg";

const ViewPredictionResultsLocators = () => {
  const dispatch = useAppDispatch();
  const predictionData = useAppSelector(getPredictionData);
  const locatorData = useAppSelector(getUploadedLocatorData);
  const predictionSubmitState = useAppSelector(getPredictionSubmitState);
  const [selectedPrediction, setSelectedPrediction] = useState<any>({
    locatorid: locatorData?.locatorid,
    typeid: undefined,
    lengthid: undefined,
    imagetype: "photo", // @TODO (for future) - address this one
  });

  const [progressStep, setProgressStep] = useState<number>(1);

  const viewPredictionRecord = (prediction: any, iterator: number) => {
    const { typeid, typename, confidence, length, path } = prediction;

    const activeClassName =
      typeid === selectedPrediction?.typeid ? "selected" : "";

    return (
      <div
        className={`prediction-item ${activeClassName}`}
        key={iterator}
        onClick={() =>
          setSelectedPrediction({ ...selectedPrediction, ...prediction })
        }
      >
        <h3>
          Prediction {iterator + 1}
          <span className="checkmark">
            <SuccessCheckmarkIcon />
          </span>
        </h3>
        <div className="record">
          <p>Locator Type</p>
          <p>{confidence}%</p>
        </div>

        <div className="loc-data">{typename}</div>
        <div className="record">
          <p>Locator Length (mm)</p>
        </div>
        <div className="loc-data">{length}</div>
        {path && (
          <div className="record">
            <p>Reference image</p>
            <img src={path} alt="" />
          </div>
        )}
      </div>
    );
  };

  const predictionMatches = predictionData?.map((prediction, iterator) =>
    viewPredictionRecord(prediction, iterator)
  );

  const onResetPrediction = () => {
    setSelectedPrediction(null);
    setProgressStep(1);
    dispatch(updatePredictionStep(1));
    dispatch(resetLocatorSlice());
    dispatch(resetPredictionSlice());
  };

  const onContinue = () =>
    progressStep >= 1 && progressStep < 3
      ? setProgressStep((prev) => prev + 1)
      : dispatch(onAddLocatorToDatasetAction(selectedPrediction));

  const onGoBack = () =>
    progressStep === 1
      ? onResetPrediction()
      : setProgressStep((prev) => prev - 1);

  const continueLabel = progressStep < 3 ? "Continue" : "Add to Dataset";
  const backlabel = progressStep === 1 ? "Cancel" : "Edit";

  const predictionControls = (
    <>
      <div className="row">
        <Button
          variant="contained"
          className="btn btn-blue"
          disabled={!selectedPrediction.typeid || !selectedPrediction.lengthid}
          onClick={onContinue}
        >
          {continueLabel}
        </Button>
      </div>
      <div className="row">
        <Button
          variant="outlined"
          className="btn btn-outlined"
          onClick={onGoBack}
        >
          {backlabel}
        </Button>
      </div>
    </>
  );

  const selectedPredictionDetails = (
    <div className="row prediction-results">
      <h3>Prediction results</h3>
      <div className="row">
        <span>Locator Type</span>
        <p>{selectedPrediction.typename}</p>
      </div>
      <div className="row">
        <span>Locator length (mm)</span>
        <p>{selectedPrediction.length}</p>
      </div>
      {selectedPrediction?.path && (
        <div className="row">
          <span>Reference image:</span>
          <img src={locatorData?.path} alt="" />
        </div>
      )}
    </div>
  );

  const locatorOptions = (
    <div className="row">
      <h2>Data labels</h2>
      <LocatorDropdownProps
        onChooseLocatorLength={(evt: any) =>
          setSelectedPrediction({
            ...selectedPrediction,
            lengthid: evt.target.value,
          })
        }
        onChooseLocatorType={(evt: any) =>
          setSelectedPrediction({
            ...selectedPrediction,
            typeid: evt.target.value,
          })
        }
        selectedLocatorLength={selectedPrediction.lengthid}
        selectedLocatorType={selectedPrediction.typeid}
      />
    </div>
  );

  const rightSideContent =
    progressStep === 2
      ? selectedPredictionDetails
      : progressStep === 3
      ? locatorOptions
      : predictionMatches;

  const predictionSubmitSuccess = (
    <div className="prediction-msg">
      <SystemMsg
        message="Successfully added to dataset!"
        messageType="success"
      />
      <div className="buttons-holder">
        <Button className="btn btn-blue" onClick={onResetPrediction}>
          Upload another locator
        </Button>
      </div>
    </div>
  );

  const predictionsViewer = (
    <div className="prediction-results-wrapper">
      <div className="left-side">
        {locatorData?.path && (
          <div className="locator-img-holder">
            <img src={locatorData.path} alt="uploaded img"/>
          </div>
        )}
        {predictionControls}
      </div>
      <div
        className="right-side"
        style={progressStep < 3 ? { display: "flex" } : {}}
      >
        {rightSideContent}
      </div>
    </div>
  );

  const predictionViewOptions =
    predictionSubmitState === "success"
      ? predictionSubmitSuccess
      : predictionsViewer;

  return predictionSubmitState === "loading" ? (
    <Loader />
  ) : predictionSubmitState === "error" ? (
    <div className="prediction-msg">
      <SystemMsg />
      <Button onClick={onResetPrediction}>Retry?</Button>
    </div>
  ) : (
    predictionViewOptions
  );
};

export default ViewPredictionResultsLocators;
